import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  addShipmentSubscriber,
  attachLabelToShipment,
  cancelShipment,
  recordManualShipmentTracking,
  createShipmentForOrder,
  createShipmentWithLines,
  getDeliveryFeedbackForm,
  getDeliveryFeedbackForShipment,
  getFulfillmentSettings,
  getLabelById,
  getOrderFulfillmentState,
  getPackingChecks,
  getShipmentLinesWithDescriptions,
  getShipment,
  getShipmentForOrder,
  getSignedPhotoUrls,
  listShipmentEvents,
  listShipmentHubs,
  listDeliveryFeedback,
  listShipmentsForOrder,
  listShipmentSubscribers,
  listShipmentTokens,
  listSaleAlertsForOrder,
  listShipments,
  listShipmentsTop,
  markOrderLinesPickedUp,
  markShipmentShipped,
  mintPublicTrackingToken,
  reconcileShipmentTracking,
  resolveSaleAlert,
  savePackingChecks,
  setShipmentLines,
  setStoreIsShipmentHub,
  unsubscribeShipmentSubscriber,
  uploadPackingPhoto,
  upsertFulfillmentSettings,
  type ActiveContext,
  type FulfillmentSubscriberChannel,
  type KeysetCursor,
  type ListDeliveryFeedbackInput,
  type ListShipmentsInput,
  type SavePackingChecksInput,
  type ShipmentLineInput,
  type UpsertDeliveryFeedbackFormInput,
  type UpsertFulfillmentSettingsInput,
  upsertDeliveryFeedbackForm,
} from '@pc/db';
import { appBus } from '@pc/core';

const K = ['fulfillment'] as const;

export function useShipmentsList(
  ctx: ActiveContext,
  input: Omit<ListShipmentsInput, 'cursor' | 'pageSize'> = {},
  options: {
    /**
     * Background refresh cadence in ms. Used by the shipment board so a
     * front-office screen left open stays current without a manual reload.
     */
    refetchInterval?: number;
  } = {},
) {
  return useInfiniteQuery({
    queryKey: [...K, ctx.orgId, ctx.storeId, 'infinite', input],
    queryFn: ({ pageParam }) =>
      listShipments(ctx, {
        ...input,
        cursor: pageParam ?? null,
        countMode: pageParam ? 'none' : (input.countMode ?? 'estimated'),
      }),
    initialPageParam: null as KeysetCursor | null,
    getNextPageParam: (last) => last.nextCursor,
    refetchInterval: options.refetchInterval,
  });
}

export function useShipmentsTop(
  ctx: ActiveContext,
  input: { status?: ListShipmentsInput['status']; limit?: number } = {},
) {
  return useQuery({
    queryKey: [...K, ctx.orgId, ctx.storeId, 'top', input],
    queryFn: () => listShipmentsTop(ctx, input),
  });
}

export function useShipment(ctx: ActiveContext, id: string | undefined) {
  return useQuery({
    queryKey: [...K, ctx.orgId, ctx.storeId, 'one', id],
    queryFn: () => getShipment(ctx, id as string),
    enabled: Boolean(id),
  });
}

export function useShipmentForOrder(
  ctx: ActiveContext,
  orderId: string | undefined,
) {
  return useQuery({
    queryKey: [...K, ctx.orgId, ctx.storeId, 'forOrder', orderId],
    queryFn: () => getShipmentForOrder(ctx, orderId as string),
    enabled: Boolean(orderId),
  });
}

export function usePackingChecks(
  ctx: ActiveContext,
  shipmentId: string | undefined,
) {
  return useQuery({
    queryKey: [...K, 'packing', shipmentId],
    queryFn: () => getPackingChecks(ctx, shipmentId as string),
    enabled: Boolean(shipmentId),
  });
}

export function useShipmentEvents(
  ctx: ActiveContext,
  shipmentId: string | undefined,
) {
  return useQuery({
    queryKey: [...K, 'events', shipmentId],
    queryFn: () => listShipmentEvents(ctx, shipmentId as string),
    enabled: Boolean(shipmentId),
  });
}

export function useShipmentSubscribers(
  ctx: ActiveContext,
  shipmentId: string | undefined,
) {
  return useQuery({
    queryKey: [...K, 'subs', shipmentId],
    queryFn: () => listShipmentSubscribers(ctx, shipmentId as string),
    enabled: Boolean(shipmentId),
  });
}

export function useShipmentTokens(
  ctx: ActiveContext,
  shipmentId: string | undefined,
) {
  return useQuery({
    queryKey: [...K, 'tokens', shipmentId],
    queryFn: () => listShipmentTokens(ctx, shipmentId as string),
    enabled: Boolean(shipmentId),
  });
}

export function useShippingLabel(
  ctx: ActiveContext,
  labelId: string | undefined,
) {
  return useQuery({
    queryKey: [...K, 'shippingLabel', labelId],
    queryFn: () => getLabelById(ctx, labelId as string),
    enabled: Boolean(labelId),
  });
}

export function useDeliveryFeedbackList(
  ctx: ActiveContext,
  input: Omit<ListDeliveryFeedbackInput, 'cursor' | 'pageSize'> = {},
) {
  return useInfiniteQuery({
    queryKey: [...K, ctx.orgId, ctx.storeId, 'feedback', input],
    queryFn: ({ pageParam }) =>
      listDeliveryFeedback(ctx, {
        ...input,
        cursor: pageParam ?? null,
        countMode: pageParam ? 'none' : (input.countMode ?? 'estimated'),
      }),
    initialPageParam: null as KeysetCursor | null,
    getNextPageParam: (last) => last.nextCursor,
  });
}

export function useShipmentDeliveryFeedback(
  ctx: ActiveContext,
  shipmentId: string | undefined,
) {
  return useQuery({
    queryKey: [...K, 'deliveryFeedback', shipmentId],
    queryFn: () => getDeliveryFeedbackForShipment(ctx, shipmentId as string),
    enabled: Boolean(shipmentId),
  });
}

export function useDeliveryFeedbackForm(ctx: ActiveContext) {
  return useQuery({
    queryKey: [...K, ctx.orgId, 'feedbackForm'],
    queryFn: () => getDeliveryFeedbackForm(ctx),
  });
}

export function useUpsertDeliveryFeedbackForm(ctx: ActiveContext) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpsertDeliveryFeedbackFormInput) =>
      upsertDeliveryFeedbackForm(ctx, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: K }),
  });
}

export function useFulfillmentSettings(ctx: ActiveContext) {
  return useQuery({
    queryKey: [...K, ctx.orgId, 'settings'],
    queryFn: () => getFulfillmentSettings(ctx),
  });
}

export function useShipmentHubs(ctx: ActiveContext) {
  return useQuery({
    queryKey: [...K, ctx.orgId, 'hubs'],
    queryFn: () => listShipmentHubs(ctx),
  });
}

export function useCreateShipmentForOrder(ctx: ActiveContext) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderId: string) => createShipmentForOrder(ctx, orderId),
    onSuccess: (shipmentId, orderId) => {
      qc.invalidateQueries({ queryKey: K });
      qc.invalidateQueries({ queryKey: ['orders'] });
      appBus().emit('fulfillment.shipment_created', { shipmentId, orderId });
    },
  });
}

export function useSavePackingChecks(ctx: ActiveContext) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SavePackingChecksInput) => savePackingChecks(ctx, input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: K });
      appBus().emit('fulfillment.packed', { shipmentId: vars.shipmentId });
    },
  });
}

export function useAttachLabelToShipment(ctx: ActiveContext) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { shipmentId: string; labelId: string }) =>
      attachLabelToShipment(ctx, input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: K });
      appBus().emit('fulfillment.label_attached', vars);
    },
  });
}

export function useMarkShipmentShipped(ctx: ActiveContext) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (shipmentId: string) => markShipmentShipped(ctx, shipmentId),
    onSuccess: (_data, shipmentId) => {
      qc.invalidateQueries({ queryKey: K });
      appBus().emit('fulfillment.shipped', { shipmentId });
    },
  });
}

export function useAddShipmentSubscriber(ctx: ActiveContext) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      shipmentId: string;
      channel: FulfillmentSubscriberChannel;
      address: string;
    }) => addShipmentSubscriber(ctx, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: K }),
  });
}

export function useUnsubscribeShipmentSubscriber(ctx: ActiveContext) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (subscriberId: string) =>
      unsubscribeShipmentSubscriber(ctx, subscriberId),
    onSuccess: () => qc.invalidateQueries({ queryKey: K }),
  });
}

export function useMintPublicTrackingToken(ctx: ActiveContext) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (shipmentId: string) => mintPublicTrackingToken(ctx, shipmentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: K }),
  });
}

export function useReconcileShipmentTracking(ctx: ActiveContext) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (shipmentId: string) => reconcileShipmentTracking(ctx, shipmentId),
    onSuccess: (result, shipmentId) => {
      qc.invalidateQueries({ queryKey: K });
      if (result.updated && result.status === 'delivered') {
        appBus().emit('fulfillment.delivered', { shipmentId });
      }
    },
  });
}

export function useUpsertFulfillmentSettings(ctx: ActiveContext) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: UpsertFulfillmentSettingsInput) =>
      upsertFulfillmentSettings(ctx, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: K }),
  });
}

export function useSetStoreIsShipmentHub(ctx: ActiveContext) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ storeId, isHub }: { storeId: string; isHub: boolean }) =>
      setStoreIsShipmentHub(ctx, storeId, isHub),
    onSuccess: () => qc.invalidateQueries({ queryKey: K }),
  });
}

export function useUploadPackingPhoto(ctx: ActiveContext) {
  return useMutation({
    mutationFn: ({ shipmentId, file }: { shipmentId: string; file: File }) =>
      uploadPackingPhoto(ctx, shipmentId, file),
  });
}

export function useSignedPhotoUrls(paths: string[]) {
  return useQuery({
    queryKey: [...K, 'photoUrls', ...paths],
    queryFn: () => getSignedPhotoUrls(paths),
    enabled: paths.length > 0,
    staleTime: 60_000 * 30,
  });
}

// =========================================================================
// Split-shipment + line-allocation hooks
// =========================================================================

/**
 * All shipments (including cancelled rows) attached to an order. Used by
 * the order detail page to render the full shipment history.
 */
export function useShipmentsForOrder(
  ctx: ActiveContext,
  orderId: string | undefined,
) {
  return useQuery({
    queryKey: [...K, ctx.orgId, ctx.storeId, 'forOrderList', orderId],
    queryFn: () => listShipmentsForOrder(ctx, orderId as string),
    enabled: Boolean(orderId),
  });
}

/**
 * Per-order fulfillment snapshot. Single source of truth for the
 * "remaining to ship" banner + per-line allocation badges.
 */
export function useOrderFulfillmentState(
  ctx: ActiveContext,
  orderId: string | undefined,
) {
  return useQuery({
    queryKey: [...K, ctx.orgId, ctx.storeId, 'state', orderId],
    queryFn: () => getOrderFulfillmentState(ctx, orderId as string),
    enabled: Boolean(orderId),
  });
}

export function useCreateShipmentWithLines(ctx: ActiveContext) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { orderId: string; lines: ShipmentLineInput[] }) =>
      createShipmentWithLines(ctx, input),
    onSuccess: (shipmentId, vars) => {
      qc.invalidateQueries({ queryKey: K });
      qc.invalidateQueries({ queryKey: ['orders'] });
      appBus().emit('fulfillment.shipment_created', {
        shipmentId,
        orderId: vars.orderId,
      });
    },
  });
}

export function useSetShipmentLines(ctx: ActiveContext) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { shipmentId: string; lines: ShipmentLineInput[] }) =>
      setShipmentLines(ctx, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: K }),
  });
}

export function useMarkOrderLinesPickedUp(ctx: ActiveContext) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      orderId: string;
      lines: Array<{ orderLineId: string; quantity: number; note?: string }>;
      pickedUpBy?: string | null;
    }) => markOrderLinesPickedUp(ctx, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: K });
      qc.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

/**
 * Lines assigned to a single shipment, joined with the order_line
 * description + allocation totals. Powers the "This shipment contains"
 * section on ShipmentDetailPage and the packing-station checklist seed.
 */
export function useShipmentLines(
  ctx: ActiveContext,
  shipmentId: string | undefined,
) {
  return useQuery({
    queryKey: [...K, ctx.orgId, ctx.storeId, 'shipmentLines', shipmentId],
    queryFn: () => getShipmentLinesWithDescriptions(ctx, shipmentId as string),
    enabled: Boolean(shipmentId),
  });
}

export function useCancelShipment(ctx: ActiveContext) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { shipmentId: string; reason?: string | null }) =>
      cancelShipment(ctx, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: K }),
  });
}

export function useRecordManualShipmentTracking(ctx: ActiveContext) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      shipmentId: string;
      carrier: string;
      trackingNumber: string;
      trackingUrl?: string | null;
      markShipped?: boolean;
    }) => recordManualShipmentTracking(ctx, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: K });
      qc.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

// =========================================================================
// Operator order alerts (orders.sale_alerts)
// =========================================================================
// Query key ['sale-alerts', orderId] is shared with the orders module so a
// resolve from the packing side refreshes the order detail too.

export function useOrderSaleAlerts(ctx: ActiveContext, orderId: string | undefined) {
  return useQuery({
    queryKey: ['sale-alerts', orderId],
    queryFn: () => listSaleAlertsForOrder(ctx, orderId as string),
    enabled: Boolean(orderId),
  });
}

export function useResolveOrderSaleAlert(ctx: ActiveContext) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; note?: string | null }) => resolveSaleAlert(ctx, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sale-alerts'] });
      // The v_shipment_summary rollup (badge counts) + order events change.
      qc.invalidateQueries({ queryKey: K });
      qc.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}
